import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Flex, Input, InputProps, Select } from 'antd';

import { EYmEvents, EYmEventsO1, EYmEventsO2, IBadRequestData } from 'shared/typings';
import { replaceUrlWithHttps, sendYmEvent } from 'shared/utils';

interface IInputUrlProps extends InputProps {
	httpsOnly?: boolean;
	errors?: (IBadRequestData | string)[];
}

export const InputUrl = ({ className, onChange, value, disabled, httpsOnly, errors, ...props }: IInputUrlProps) => {
	const { t } = useTranslation();

	const { url: urlValue, protocol: protocolValue } = replaceUrlWithHttps(value?.toString() || '');
	const [protocol, setProtocol] = useState<'https://' | 'http://'>(
		httpsOnly ? 'https://' : protocolValue || 'https://',
	);

	useEffect(() => {
		urlValue && setProtocol(protocolValue || 'https://');
	}, [protocolValue, urlValue]);

	const [isRestrictedDomain, setIsRestrictedDomain] = useState(false);

	useEffect(() => {
		if (errors) {
			const hasRestrictedDomain = errors
				?.map((error) => (typeof error === 'object' && 'code' in error ? error.code : error))
				.includes('domain_is_restricted');

			hasRestrictedDomain &&
				sendYmEvent({
					event: EYmEvents.ERROR_BLACKLIST_ADSPACE,
					o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.ADSPACE],
					o2: EYmEventsO2.ADD_ADSPACE,
					o4: urlValue,
				});

			setIsRestrictedDomain(hasRestrictedDomain);
		}
	}, [errors]); // eslint-disable-line

	return (
		<Flex vertical gap={8}>
			<Input
				{...props}
				className={className}
				addonBefore={
					httpsOnly ? (
						'https://'
					) : (
						<Select
							value={protocol}
							onChange={(v) => {
								setProtocol(v);
								onChange?.({
									target: { value: (v || protocol) + urlValue },
								} as ChangeEvent<HTMLInputElement>);
							}}
							disabled={disabled}>
							<Select.Option value="https://">https://</Select.Option>
							<Select.Option value="http://">http://</Select.Option>
						</Select>
					)
				}
				onChange={(e) => {
					const { url, protocol: newProtocol } = replaceUrlWithHttps(e.target.value);
					newProtocol && url && setProtocol(httpsOnly ? 'https://' : newProtocol);
					e.target.value = (!url ? '' : httpsOnly ? 'https://' : newProtocol || protocol) + url;
					onChange?.(e);
					setIsRestrictedDomain(false);
				}}
				value={urlValue}
				disabled={disabled}
				status={isRestrictedDomain ? 'error' : undefined}
			/>

			{isRestrictedDomain && (
				<Alert type="error" showIcon message={t('Данная площадка запрещена условиями сотрудничества')} />
			)}
		</Flex>
	);
};
